import {
  Ban,
  CalendarClock,
  CheckCheck,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleX,
  Clock,
  LoaderCircle,
  Mail,
  Megaphone,
  Phone,
  Tag,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  CAMPAIGN_LIST_PAGE_SIZES,
  CAMPAIGN_RECIPIENT_PAGE_SIZES,
  DEFAULT_CAMPAIGN_LIST_PAGE_SIZE,
  DEFAULT_CAMPAIGN_RECIPIENT_LIST_LIMIT,
  isCampaignListNavStatus,
  serializeCampaignListSortParam,
  uniqueCampaignListSortRules,
  type CampaignAudienceDefinition,
  type CampaignAudienceImportStatusName,
  type CampaignAudienceIssueReason,
  type CampaignAudienceSnapshotSummary,
  type CampaignAudienceUploadDto,
  type CampaignAudienceUploadIssueReason,
  type CampaignDto,
  type CampaignListItem,
  type CampaignListNavStatus,
  type CampaignListSortRule,
  type CampaignListStatusCounts,
  type CampaignPersonalizationSnapshot,
  type CampaignRecipientListItem,
  type CampaignSourceProvenance,
  type CampaignStatusName,
  type ContactListItem,
  type CountryDialCode,
  type ConfigureCampaignAudienceUploadRequest,
  campaignListQuerySchema,
} from "@multi-chat/shared";
import { campaignResultStatusChipClass } from "./campaign-results-presentation.js";

export { CAMPAIGN_RECIPIENT_PAGE_SIZES };
export const CAMPAIGN_IMPORT_POLL_MS = 1500;
export const CAMPAIGN_RECIPIENT_PAGE_SIZE = DEFAULT_CAMPAIGN_RECIPIENT_LIST_LIMIT;
export const CAMPAIGN_DEFAULT_COUNTRY_ISO = "IL";
export const CAMPAIGN_SUBNAV_WIDTH_CLASS = "w-56";
/** Toolbar / header control that opens the campaign status drawer. Matches Sort/Columns height. */
export const CAMPAIGN_STATUS_DRAWER_TRIGGER_CLASS =
  "h-8 max-w-[14rem] shrink-0 gap-1.5 border-line/70 px-2.5 text-[0.75rem]";
export const CAMPAIGN_PANE_MOTION_MS = 180;

/**
 * Content workspace. Logical columns: the form pane sits at the inline start
 * (right in Hebrew, left in English) and the preview pane at the inline end.
 * Desktop height: viewport minus the draft shell header + stepper (~6rem), page
 * padding (~2.5rem), the Content section header (~3.25rem) and its body padding
 * plus continue hint (~2.25rem), so the section header and Save stay in view.
 * Mobile order: form, preview toolbar, phone — natural page flow, no inner scroll.
 */
export const campaignContentLayoutClass =
  "grid min-w-0 gap-4 [grid-template-areas:'form'_'template'_'preview'] [grid-template-columns:minmax(0,1fr)] lg:h-[clamp(30rem,calc(100dvh-14rem),52rem)] lg:grid-cols-[minmax(0,1.35fr)_minmax(20rem,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:gap-x-5 lg:[grid-template-areas:'form_aside']";

export const campaignContentFormClass =
  "flex min-w-0 flex-col rounded-xl border border-line/70 bg-surface shadow-[0_1px_2px_rgb(15_23_42/0.04)] [grid-area:form] lg:min-h-0 lg:overflow-hidden";

/** Desktop: the form pane owns the only vertical scroll in the Content workspace. */
export const campaignContentFormScrollClass =
  "min-w-0 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain lg:[scrollbar-gutter:stable] lg:[scrollbar-width:thin] lg:[scrollbar-color:var(--color-line-strong)_transparent]";

/** The panel stays full width; the form content keeps a deliberate start-aligned width. */
export const campaignContentFormBodyClass =
  "grid w-full min-w-0 max-w-[38rem] content-start gap-6 px-4 pb-5 pt-4";

/** Section-level continue hint, outside the Content workspace. */
export const campaignContentSaveClass = "grid min-w-0 content-start empty:hidden";

/**
 * `contents` on mobile so the toolbar and phone fall into their own grid areas.
 * Desktop: fixed beside the form; scrolls as one unit only when the viewport is too short.
 */
export const campaignContentAsideClass =
  "contents lg:flex lg:min-h-0 lg:min-w-0 lg:flex-col lg:gap-3 lg:overflow-y-auto lg:overscroll-contain lg:rounded-xl lg:border lg:border-line/60 lg:bg-surface-muted/50 lg:p-4 lg:[grid-area:aside] lg:[scrollbar-width:thin] lg:[scrollbar-color:var(--color-line-strong)_transparent]";

/** Template + preview recipient. Side by side only when the pane is wide enough. */
export const campaignContentToolbarClass =
  "@container min-w-0 shrink-0 max-lg:[grid-area:template]";

export const campaignContentToolbarRowClass =
  "grid min-w-0 items-center gap-2 @[30rem]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]";

export const campaignContentPreviewAreaClass =
  "grid min-w-0 shrink-0 gap-3 max-lg:[grid-area:preview]";

/** Thin, subtle internal scrollbar for Campaign dropdowns and the phone chat. */
export const campaignScrollAreaClass =
  "overflow-y-auto overscroll-contain [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:var(--color-line-strong)_transparent]";

/**
 * Two-line identity blocks (name over phone) align by UI locale, not by the
 * detected direction of the name, so an English name stays right-aligned in Hebrew.
 */
export function campaignLocaleAlignClass(uiDir: string): string {
  return uiDir === "rtl" ? "text-right items-end" : "text-left items-start";
}

/** Opt-in menu cap for Campaign selects. Do not apply this on shared Select by default. */
export const campaignSelectMenuProps = {
  viewportClassName:
    "max-h-[min(20rem,var(--radix-select-content-available-height))] overflow-y-auto overscroll-contain",
  collisionPadding: 8,
} as const;
export const CAMPAIGN_PANE_OFFSET_PX = 3;
export const CAMPAIGN_LIST_SEARCH_DEBOUNCE_MS = 280;
export const CAMPAIGN_LIST_SEARCH_MAX_LENGTH = 200;

export type CampaignCsvWizardStep = "upload" | "configure" | "validate";
export type CampaignManageView = "hub" | "contacts" | "groups" | "csv" | "review";
export type CampaignManageSourceTab = "contacts" | "groups" | "csv";
export type CurrentPageSelectionState = "none" | "some" | "all";

export function campaignManageSourceTab(view: CampaignManageView): CampaignManageSourceTab {
  switch (view) {
    case "groups":
      return "groups";
    case "csv":
      return "csv";
    case "hub":
    case "contacts":
    case "review":
      return "contacts";
    default: {
      const _exhaustive: never = view;
      return _exhaustive;
    }
  }
}

export function issueTitleI18nKey(
  reason: CampaignAudienceIssueReason | CampaignAudienceUploadIssueReason | string,
): string {
  return `campaigns.issues.titles.${reason}`;
}

export function currentPageSelectionState(
  items: ReadonlyArray<Pick<ContactListItem, "id">>,
  selectedIds: ReadonlySet<number>,
): CurrentPageSelectionState {
  if (items.length === 0) return "none";

  let selectedCount = 0;
  for (const item of items) {
    if (selectedIds.has(item.id)) selectedCount += 1;
  }

  if (selectedCount === 0) return "none";
  return selectedCount === items.length ? "all" : "some";
}

export function toggleCurrentPageSelection(
  items: ReadonlyArray<Pick<ContactListItem, "id">>,
  selectedIds: ReadonlySet<number>,
): Set<number> {
  const next = new Set(selectedIds);
  const removeCurrentPage = currentPageSelectionState(items, selectedIds) === "all";

  for (const item of items) {
    if (removeCurrentPage) next.delete(item.id);
    else next.add(item.id);
  }

  return next;
}

export type CampaignPageSelectionBanner =
  | { kind: "hidden" }
  | { kind: "page"; pageCount: number; total: number }
  | { kind: "all"; total: number }
  | { kind: "partial"; selected: number; total: number };

/** Page vs all-loaded selection copy. The ID set itself stays explicit. */
export function campaignPageSelectionBanner(input: {
  selectedCount: number;
  pageCount: number;
  pageFullySelected: boolean;
  totalCount: number;
}): CampaignPageSelectionBanner {
  if (input.selectedCount <= 0 || input.totalCount <= 0) return { kind: "hidden" };
  if (input.selectedCount === input.totalCount) return { kind: "all", total: input.totalCount };
  if (
    input.pageFullySelected &&
    input.pageCount > 0 &&
    input.selectedCount === input.pageCount &&
    input.totalCount > input.pageCount
  ) {
    return { kind: "page", pageCount: input.pageCount, total: input.totalCount };
  }
  if (input.selectedCount < input.totalCount) {
    return { kind: "partial", selected: input.selectedCount, total: input.totalCount };
  }
  return { kind: "hidden" };
}

export function syncCheckboxIndeterminate(
  node: Pick<HTMLInputElement, "indeterminate"> | null,
  state: CurrentPageSelectionState,
): void {
  if (node) node.indeterminate = state === "some";
}

export function canMutateCampaignAudience(role: string): boolean {
  return role === "OWNER" || role === "ADMIN";
}

export function shouldPollCampaignImport(
  status: CampaignAudienceImportStatusName | null,
): boolean {
  return status === "UPLOADED" || status === "PARSING";
}

export function isCampaignImportReady(
  status: CampaignAudienceImportStatusName | null,
): boolean {
  return status === "READY";
}

export function campaignStatusBadgeVariant(
  status: CampaignStatusName,
): "default" | "success" | "warning" | "error" {
  switch (status) {
    case "DRAFT":
      return "default";
    case "SCHEDULED":
      return "warning";
    case "RUNNING":
      return "warning";
    case "COMPLETED":
      return "success";
    case "CANCELLED":
      return "default";
    case "FAILED":
      return "error";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function campaignStatusIcon(status: CampaignStatusName): LucideIcon {
  switch (status) {
    case "DRAFT":
      return CircleDashed;
    case "SCHEDULED":
      return CalendarClock;
    case "RUNNING":
      return LoaderCircle;
    case "COMPLETED":
      return CheckCheck;
    case "FAILED":
      return CircleX;
    case "CANCELLED":
      return Ban;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function campaignStatusChipClass(status: CampaignStatusName): string {
  switch (status) {
    case "DRAFT":
      return "border-line/70 bg-surface-muted/70 text-muted";
    case "SCHEDULED":
      return "border-accent/20 bg-accent/10 text-accent";
    case "RUNNING":
      return campaignResultStatusChipClass("PROCESSING");
    case "COMPLETED":
      return "border-success/25 bg-success-muted text-success";
    case "FAILED":
      return "border-error/25 bg-error-muted text-error";
    case "CANCELLED":
      return "border-line/70 bg-surface-muted text-muted";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function campaignHasAudienceSources(
  definition: CampaignAudienceDefinition,
): boolean {
  return (
    definition.contactIds.length > 0 ||
    definition.contactGroupIds.length > 0 ||
    Boolean(definition.upload?.uploadId)
  );
}

export function campaignSourceCounts(definition: CampaignAudienceDefinition): {
  contacts: number;
  groups: number;
  csv: number;
} {
  return {
    contacts: definition.contactIds.length,
    groups: definition.contactGroupIds.length,
    csv: definition.upload?.uploadId ? 1 : 0,
  };
}

export function campaignListItemFromDto(campaign: CampaignDto): CampaignListItem {
  return {
    id: campaign.id,
    name: campaign.name,
    status: campaign.status,
    snapshotStale: campaign.snapshotStale,
    readyRecipientCount: campaign.snapshotStale
      ? null
      : (campaign.audienceSnapshotSummary?.readyRecipients ?? null),
    scheduledAt: campaign.scheduledAt,
    createdAt: campaign.createdAt,
    updatedAt: campaign.updatedAt,
  };
}

export function insertCreatedCampaignInList(
  items: readonly CampaignListItem[],
  created: CampaignListItem,
  pageSize: number,
): CampaignListItem[] {
  const createdId = Number(created.id);
  const withoutDuplicate = items.filter((item) => Number(item.id) !== createdId);
  return [created, ...withoutDuplicate].slice(0, pageSize);
}

/**
 * Local numbers cannot be validated until a default country is chosen.
 * READY uploads with a null country still belong on the configuration step —
 * that was the production bug (MISSING_COUNTRY with no visible selector).
 */
export function csvNeedsConfiguration(upload: CampaignAudienceUploadDto): boolean {
  return (
    upload.headerConfirmationRequired ||
    upload.phoneColumnSelectionRequired ||
    upload.defaultCountryIso == null
  );
}

export function campaignCsvWizardStep(
  upload: CampaignAudienceUploadDto | null,
): CampaignCsvWizardStep {
  if (!upload) return "upload";
  switch (upload.status) {
    case "UPLOADED":
    case "PARSING":
    case "FAILED":
    case "EXPIRED":
    case "DELETED":
      return "upload";
    case "AWAITING_CONFIGURATION":
      return "configure";
    case "READY":
      return csvNeedsConfiguration(upload) ? "configure" : "validate";
    case "CONSUMED":
      return "validate";
    default: {
      const _exhaustive: never = upload.status;
      return _exhaustive;
    }
  }
}

export function csvDefaultCountryVisible(
  upload: CampaignAudienceUploadDto | null,
): boolean {
  if (!upload) return false;
  return campaignCsvWizardStep(upload) === "configure";
}

export function csvShowsPhoneColumn(upload: CampaignAudienceUploadDto): boolean {
  const headerCount = upload.headers?.headers.length ?? 0;
  return headerCount > 1 || upload.phoneColumnSelectionRequired;
}

export function buildCsvConfigureRequest(input: {
  defaultCountryIso: string;
  phoneColumnKey: string;
  hasHeader: boolean | null;
}): ConfigureCampaignAudienceUploadRequest {
  const body: ConfigureCampaignAudienceUploadRequest = {
    defaultCountryIso: input.defaultCountryIso,
  };
  if (input.phoneColumnKey.length > 0) body.phoneColumnKey = input.phoneColumnKey;
  if (input.hasHeader != null) body.hasHeader = input.hasHeader;
  return body;
}

/** True only after a snapshot was valid, then the definition changed. */
export function audienceNeedsReview(input: {
  snapshotStale: boolean;
  hadConfirmedSnapshot: boolean;
}): boolean {
  return input.snapshotStale && input.hadConfirmedSnapshot;
}

/**
 * Combined preview/commit require a READY or CONSUMED (DRAFT-retained) CSV.
 * Processing / failed / expired uploads still block until resolved.
 */
export function csvBlocksAudiencePreview(
  status: CampaignAudienceImportStatusName | null,
): boolean {
  return (
    status === "UPLOADED" ||
    status === "PARSING" ||
    status === "AWAITING_CONFIGURATION" ||
    status === "FAILED" ||
    status === "EXPIRED"
  );
}

const UPLOADED_DISPLAY_NAME_KEYS = [
  "displayname",
  "display_name",
  "name",
  "fullname",
  "full_name",
];
const UPLOADED_FIRST_NAME_KEYS = ["firstname", "first_name"];
const UPLOADED_LAST_NAME_KEYS = ["lastname", "last_name"];

function uploadFieldValue(
  fields: Readonly<Record<string, string>>,
  keys: readonly string[],
): string {
  const entries = Object.entries(fields);
  for (const key of keys) {
    const found = entries.find(
      ([fieldKey, value]) => fieldKey.toLowerCase() === key && value.trim().length > 0,
    );
    if (found) return found[1].trim();
  }
  return "";
}

export function uploadedRecipientName(uploadFields: Readonly<Record<string, string>>): string {
  const display = uploadFieldValue(uploadFields, UPLOADED_DISPLAY_NAME_KEYS);
  if (display.length > 0) return display;
  const first = uploadFieldValue(uploadFields, UPLOADED_FIRST_NAME_KEYS);
  const last = uploadFieldValue(uploadFields, UPLOADED_LAST_NAME_KEYS);
  return [first, last].filter(Boolean).join(" ").trim();
}

export function recipientDisplayName(
  item: Pick<CampaignRecipientListItem, "personalizationSnapshot" | "normalizedPhoneE164">,
): string {
  const fields = item.personalizationSnapshot.contactFields;
  const contactName =
    fields.displayName?.trim() ||
    [fields.firstName, fields.lastName].filter(Boolean).join(" ").trim();
  if (contactName.length > 0) return contactName;
  const uploaded = uploadedRecipientName(item.personalizationSnapshot.uploadFields);
  return uploaded.length > 0 ? uploaded : "—";
}

export function recipientRangeBounds(input: {
  offset: number;
  count: number;
}): { start: number; end: number } {
  if (input.count === 0) return { start: 0, end: 0 };
  return { start: input.offset + 1, end: input.offset + input.count };
}

export function recipientEmail(
  snapshot: CampaignPersonalizationSnapshot,
): string | null {
  const email = snapshot.contactFields.email?.trim();
  if (email) return email;
  const uploadEmail =
    snapshot.uploadFields.email?.trim() || snapshot.uploadFields.Email?.trim();
  return uploadEmail && uploadEmail.length > 0 ? uploadEmail : null;
}

export type CampaignSourceChip =
  | { kind: "CONTACT" }
  | { kind: "GROUP"; groupName: string }
  | { kind: "UPLOAD" };

export function recipientSourceChips(
  provenance: CampaignSourceProvenance,
): CampaignSourceChip[] {
  const chips: CampaignSourceChip[] = [];
  const seenGroups = new Set<string>();
  let sawContact = false;
  let sawUpload = false;
  for (const source of provenance.sources) {
    switch (source.type) {
      case "CONTACT":
        if (!sawContact) {
          chips.push({ kind: "CONTACT" });
          sawContact = true;
        }
        break;
      case "GROUP":
        if (!seenGroups.has(source.groupName)) {
          seenGroups.add(source.groupName);
          chips.push({ kind: "GROUP", groupName: source.groupName });
        }
        break;
      case "UPLOAD":
        if (!sawUpload) {
          chips.push({ kind: "UPLOAD" });
          sawUpload = true;
        }
        break;
      default: {
        const _exhaustive: never = source;
        return _exhaustive;
      }
    }
  }
  return chips;
}

export function collapsedUploadFields(
  snapshot: CampaignPersonalizationSnapshot,
): Array<{ key: string; value: string }> {
  return Object.entries(snapshot.uploadFields)
    .filter(([, value]) => value.trim().length > 0)
    .map(([key, value]) => ({ key, value }));
}

export function issueReasonI18nKey(
  reason: CampaignAudienceIssueReason | CampaignAudienceUploadIssueReason | string,
): string {
  return `campaigns.issues.${reason}`;
}

export function previewMetricRows(summary: CampaignAudienceSnapshotSummary): Array<{
  key:
    | "selectedSourceEntries"
    | "canonicalRecipients"
    | "readyRecipients"
    | "excludedBeforeRecipient"
    | "duplicateSourceEntries";
  value: number;
}> {
  return [
    { key: "selectedSourceEntries", value: summary.selectedSourceEntries },
    { key: "canonicalRecipients", value: summary.canonicalRecipients },
    { key: "readyRecipients", value: summary.readyRecipients },
    { key: "excludedBeforeRecipient", value: summary.excludedBeforeRecipient },
    { key: "duplicateSourceEntries", value: summary.duplicateSourceEntries },
  ];
}

export function summaryReasonEntries(
  byReason: Readonly<Partial<Record<string, number>>>,
): Array<{ reason: string; count: number }> {
  return Object.entries(byReason)
    .filter((entry): entry is [string, number] => typeof entry[1] === "number" && entry[1] > 0)
    .map(([reason, count]) => ({ reason, count }));
}

export function formatCampaignDate(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

export type CampaignListColumnId = "name" | "status" | "audience" | "updated" | "scheduled";
export type CampaignRecipientColumnId = "name" | "phone" | "email" | "sources";

export const CAMPAIGN_LIST_COLUMN_META: Record<
  CampaignListColumnId,
  { icon: LucideIcon }
> = {
  name: { icon: Megaphone },
  status: { icon: CircleDot },
  audience: { icon: Users },
  updated: { icon: Clock },
  scheduled: { icon: CalendarClock },
};

export const CAMPAIGN_RECIPIENT_COLUMN_META: Record<
  CampaignRecipientColumnId,
  { icon: LucideIcon }
> = {
  name: { icon: User },
  phone: { icon: Phone },
  email: { icon: Mail },
  sources: { icon: Tag },
};

export type CampaignListViewQuery = {
  status?: CampaignListNavStatus;
  search: string;
  page: number;
  pageSize: number;
  sort?: CampaignListSortRule[];
};

export const CAMPAIGN_LIST_NAV_ITEMS = [
  {
    id: "all",
    status: undefined,
    icon: Megaphone,
    labelKey: "campaigns.nav.all",
    countKey: "all",
    testId: "campaign-subnav-all",
  },
  {
    id: "DRAFT",
    status: "DRAFT",
    icon: CircleDashed,
    labelKey: "campaigns.nav.drafts",
    countKey: "DRAFT",
    testId: "campaign-subnav-drafts",
  },
  {
    id: "SCHEDULED",
    status: "SCHEDULED",
    icon: CalendarClock,
    labelKey: "campaigns.nav.scheduled",
    countKey: "SCHEDULED",
    testId: "campaign-subnav-scheduled",
  },
  {
    id: "RUNNING",
    status: "RUNNING",
    icon: LoaderCircle,
    labelKey: "campaigns.nav.running",
    countKey: "RUNNING",
    testId: "campaign-subnav-running",
  },
  {
    id: "COMPLETED",
    status: "COMPLETED",
    icon: CircleCheck,
    labelKey: "campaigns.nav.completed",
    countKey: "COMPLETED",
    testId: "campaign-subnav-completed",
  },
] as const;

function parsePositiveInt(raw: string | null, fallback: number): number {
  if (raw == null || raw.trim() === "") return fallback;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) return fallback;
  return parsed;
}

export function parseCampaignListViewQuery(search: string): CampaignListViewQuery {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const statusRaw = params.get("status");
  const searchText = (params.get("search") ?? "").trim().slice(0, CAMPAIGN_LIST_SEARCH_MAX_LENGTH);
  const page = parsePositiveInt(params.get("page"), 1);
  const pageSizeRaw = Number(params.get("pageSize"));
  const pageSize = (CAMPAIGN_LIST_PAGE_SIZES as readonly number[]).includes(pageSizeRaw)
    ? pageSizeRaw
    : DEFAULT_CAMPAIGN_LIST_PAGE_SIZE;
  const sortParsed = campaignListQuerySchema.pick({ sort: true }).safeParse({
    sort: params.get("sort") ?? undefined,
  });
  return {
    ...(isCampaignListNavStatus(statusRaw) ? { status: statusRaw } : {}),
    search: searchText,
    page,
    pageSize,
    sort: sortParsed.success
      ? uniqueCampaignListSortRules(sortParsed.data.sort ?? [])
      : [],
  };
}

export function campaignListHref(query: CampaignListViewQuery): string {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.search.trim().length > 0) params.set("search", query.search.trim());
  const sort = serializeCampaignListSortParam(query.sort ?? []);
  if (sort != null) params.set("sort", sort);
  if (query.page > 1) params.set("page", String(query.page));
  if (query.pageSize !== DEFAULT_CAMPAIGN_LIST_PAGE_SIZE) {
    params.set("pageSize", String(query.pageSize));
  }
  const qs = params.toString();
  return qs.length > 0 ? `/campaigns?${qs}` : "/campaigns";
}

export function campaignVisibleInListQuery(
  item: Pick<CampaignListItem, "name" | "status">,
  query: Pick<CampaignListViewQuery, "status" | "search">,
): boolean {
  if (query.status != null && item.status !== query.status) return false;
  const needle = query.search.trim().toLowerCase();
  if (needle.length > 0 && !item.name.toLowerCase().includes(needle)) return false;
  return true;
}

export function campaignListNavCount(
  counts: CampaignListStatusCounts,
  key: (typeof CAMPAIGN_LIST_NAV_ITEMS)[number]["countKey"],
): number {
  return counts[key];
}

export function isCsvFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith(".csv") || file.type === "text/csv" || file.type === "application/csv";
}

export function isCampaignAudienceUploadFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv") || name.endsWith(".xlsx") || (name.endsWith(".xls") && !name.endsWith(".xlsx"))) {
    return true;
  }
  return isCsvFile(file);
}

export function resetFileInputValue(input: Pick<HTMLInputElement, "value">): void {
  input.value = "";
}

function countryFlagEmoji(code: string): string {
  return code
    .toUpperCase()
    .replace(/[A-Z]/g, (letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)));
}

export function campaignCountryDisplayText(
  country: Pick<CountryDialCode, "code" | "name" | "dialCode">,
  localizedName = country.name,
): string {
  return `${countryFlagEmoji(country.code)} ${localizedName} · ${country.dialCode}`;
}

export function campaignCountryCompactText(
  country: Pick<CountryDialCode, "code" | "dialCode">,
): string {
  return `${countryFlagEmoji(country.code)} ${country.dialCode}`;
}
